import { Box } from '@mui/material';
import { useEffect, useMemo, type RefObject } from 'react';

import {
  CaseCardLinkLines,
  HubsViewMode,
  Loader,
  US_CENTER_COORDS,
  type Point,
} from '#shared';

import { AircraftDrawer } from '../../AircraftDrawer';
import { CaseDetailsDrawer, useCaseSelection } from '../../CaseDrawer';
import {
  useAircraftSelection,
  useAppBehavior,
  useData,
  useProjectionChangingWithThreshold,
  ZLayersProvider,
} from '../../data-context';
import { EFocusView, useGeoMap } from '../../GeoMap';
import { useAppDispatch } from '../../redux-store';
import { relationsActions, toolbarSelectors } from '../../slices';
import { useCaseLabelPosition, useMainMapOverlay } from '../model';

import { CaseLayer } from './CaseLayer';
import { ClustersSvgLayer } from './ClustersLayout';
import { HubsDotsOnMap } from './HubsDotsOnMap';
import { MapOverlayStyled } from './MapOverlay.styled';
import { MapOverlayFooterPanel } from './MapOverlayFooterPanel';
import { NewAlertLine } from './NewAlertLine';
import { OverlayShadows } from './OverlayShadows';

interface MainMapOverlayProps {
  renderInCentralWidget?: boolean;
  withTimerInside?: boolean;
  initialFocus?: EFocusView;
}

export const MapOverlay = (props: MainMapOverlayProps) => {
  const {
    renderInCentralWidget = false,
    withTimerInside = false,
    initialFocus = EFocusView.US_CONTINENTAL,
  } = props;
  const {
    open: isAircraftDrawerOpen,
    selectedAircraft,
    handleClose: handleAircraftDrawerClose,
    handleSelectAircraft,
  } = useAircraftSelection();
  const {
    open: isCaseDrawerOpen,
    selectedCase,
    handleClose: handleCaseDrawerClose,
    selectedCaseType,
    handleSelectCaseById,
  } = useCaseSelection();
  const {
    hasContent,
    newCasesOnMap,
    clusters,
    cases,
    interactionLayerRef,
    hubs,
  } = useMainMapOverlay();
  const dispatch = useAppDispatch();
  const { projectionRef, isInitialized } = useGeoMap();
  const { appDataErrors, lastUpdateTime } = useData();
  const { highlightStatus, shouldApplyHighlight } = useAppBehavior();
  const hubsView = toolbarSelectors.useSelectHubsView();
  const showAircraftsOnFly = toolbarSelectors.useSelectShowAircraftsOnFly();
  const labelPositions = useCaseLabelPosition();

  const isProjectionChange = useProjectionChangingWithThreshold({
    rotEpsDeg: 0.35,
    scaleEps: 0.75,
    endDebounceMs: 100,
  });

  // reusable hashmap of new case IDs to their current positions on the map
  const newCaseEntries = useMemo(() => {
    const entries: Record<string, Point> = {};
    if (isProjectionChange) {
      return entries;
    }

    newCasesOnMap.forEach(nc => {
      const p = nc?.donorHospital?.lonLat as Point | undefined;
      if (p) {
        const [x, y] = projectionRef.current(p) as Point;
        entries[nc.id] = [x, y];
      }
    });
    return entries;
  }, [newCasesOnMap, projectionRef, isProjectionChange]);

  useEffect(() => {
    const isMapDataReady =
      isInitialized && projectionRef && renderInCentralWidget;

    if (!isMapDataReady || isProjectionChange) {
      return;
    }
    // TODO consider to calculate center from viewport center instead of fixed US center
    const geoMapCenter = projectionRef.current(US_CENTER_COORDS) ?? [0, 0];
    dispatch(relationsActions.setNewCasesOnMap(newCaseEntries));
    dispatch(relationsActions.setGeoMapCenter(geoMapCenter));
  }, [
    dispatch,
    isInitialized,
    newCaseEntries,
    projectionRef,
    renderInCentralWidget,
    isProjectionChange,
  ]);

  const isFlatView = hubsView === HubsViewMode.Flat;
  const isRegionsView = hubsView === HubsViewMode.Regions;
  const renderShadow = renderInCentralWidget && (isRegionsView || isFlatView);
  const activeRotationStatus = renderInCentralWidget
    ? highlightStatus
    : undefined;
  const isRotationActive = !renderInCentralWidget
    ? false
    : shouldApplyHighlight;

  return (
    <MapOverlayStyled isFlat={isFlatView}>
      {(!isInitialized || !hasContent) && <Loader />}
      {renderShadow && (
        <OverlayShadows
          variant={isFlatView ? 'bottom' : 'all'}
          length={'long'}
        />
      )}
      <Box
        component="svg"
        id="mainOverlaySVG"
        ref={interactionLayerRef as RefObject<SVGSVGElement>}
        height="100%"
        width="100%"
        // this attribute toggles the "hide links" rule above
        data-projection-changing={isProjectionChange ? '' : undefined}
      >
        <ZLayersProvider>
          <defs>
            <linearGradient id="eastAlertLineGradient">
              <stop offset="36%" stopColor="rgba(67, 76, 92, 0.5)" />
              <stop offset="65%" stopColor="rgba(255, 174, 129, 0.5)" />
            </linearGradient>
            <linearGradient id="westAlertLineGradient">
              <stop offset="36%" stopColor="rgba(255, 174, 129, 0.5)" />
              <stop offset="65%" stopColor="rgba(67, 76, 92, 0.5)" />
            </linearGradient>
            <linearGradient
              id="hubLineGradient"
              x1="381.5"
              y1="-87.9994"
              x2="3.2088"
              y2="152.218"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#406074" />
              <stop offset="1" stopColor="#1E2D3A" />
            </linearGradient>
            {/* Dynamic flight gradients will be added programmatically */}
          </defs>
          {isInitialized && !isProjectionChange && (
            <>
              {newCasesOnMap.map(item => (
                <NewAlertLine
                  key={item.id}
                  id={item.id}
                  organType={item.organ.name}
                  position={newCaseEntries[item.id]}
                  activeRotationStatus={activeRotationStatus}
                  isRotationActive={isRotationActive}
                />
              ))}
              {[...cases]
                .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
                .map(caseItem => (
                  <CaseLayer
                    lastUpdateTime={lastUpdateTime}
                    key={caseItem.id}
                    caseItem={caseItem}
                    activeRotationStatus={activeRotationStatus}
                    isRotationActive={isRotationActive}
                    showAircraft={showAircraftsOnFly}
                    onAircraftSelect={handleSelectAircraft}
                  />
                ))}
              <HubsDotsOnMap hubs={hubs} />
              <ClustersSvgLayer
                clusters={clusters}
                handleSelectCase={handleSelectCaseById}
                activeRotationStatus={activeRotationStatus}
                isRotationActive={isRotationActive}
                labelPositions={labelPositions}
              />

              {/* CARD to DOT CONNECTOR */}
              {renderInCentralWidget && (
                <CaseCardLinkLines
                  svgRef={interactionLayerRef as RefObject<SVGSVGElement>}
                  ids={newCasesOnMap.map(n => n.id)}
                  pointsById={newCaseEntries}
                  moving={isProjectionChange}
                />
              )}
            </>
          )}
        </ZLayersProvider>
      </Box>
      {/* Hubs and Surgeons with AppStatus/Legend/TechInfo */}
      <MapOverlayFooterPanel
        renderInCentalWidget={renderInCentralWidget}
        initialFocus={initialFocus}
        lastUpdateTime={lastUpdateTime}
        appDataErrors={appDataErrors}
        hubs={hubs}
        hubsViewMode={hubsView}
        withTimerInside={withTimerInside}
        isProjectionChange={isProjectionChange}
      />

      {/* Drawers */}
      <CaseDetailsDrawer
        open={isCaseDrawerOpen}
        onClose={handleCaseDrawerClose}
        caseData={selectedCase}
        selectedCaseType={selectedCaseType}
      />
      <AircraftDrawer
        open={isAircraftDrawerOpen}
        onClose={handleAircraftDrawerClose}
        aircraftData={selectedAircraft}
      >
        <AircraftDrawer.AircraftEvents />
        <AircraftDrawer.Location />
        <AircraftDrawer.Timeline />
        <AircraftDrawer.Weather />
      </AircraftDrawer>
    </MapOverlayStyled>
  );
};
